import { getApiBaseUrl } from '../config/constants';

const API_BASE_URL = getApiBaseUrl();

export interface UserProgramPreferences {
  userId: string;
  selectedPrograms: string[]; // Array of program IDs (e.g., ['saa', 'aa'])
  primaryProgram?: string; // The main program they're working on
  lastUpdated: string;
}

export class UserProgramPreferencesService {
  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Get user's program preferences
   */
  static async getUserProgramPreferences(): Promise<UserProgramPreferences | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/program-preferences`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        // No preferences set yet
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch program preferences: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user program preferences:', error);
      return null;
    }
  }

  /**
   * Save user's program preferences
   */
  static async saveUserProgramPreferences(preferences: Omit<UserProgramPreferences, 'userId' | 'lastUpdated'>): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/program-preferences`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error(`Failed to save program preferences: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving user program preferences:', error);
      throw error;
    }
  }

  /**
   * Update user's program preferences
   */
  static async updateUserProgramPreferences(preferences: Partial<Omit<UserProgramPreferences, 'userId' | 'lastUpdated'>>): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/program-preferences`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error(`Failed to update program preferences: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating user program preferences:', error);
      throw error;
    }
  }

  /**
   * Add a program to user's preferences
   */
  static async addProgramToPreferences(programId: string): Promise<void> {
    try {
      const currentPreferences = await this.getUserProgramPreferences();
      const selectedPrograms = currentPreferences?.selectedPrograms || [];
      
      if (!selectedPrograms.includes(programId)) {
        const updatedPreferences = {
          selectedPrograms: [...selectedPrograms, programId],
          primaryProgram: currentPreferences?.primaryProgram || programId
        };
        
        if (currentPreferences) {
          await this.updateUserProgramPreferences(updatedPreferences);
        } else {
          await this.saveUserProgramPreferences(updatedPreferences);
        }
      }
    } catch (error) {
      console.error('Error adding program to preferences:', error);
      throw error;
    }
  }

  /**
   * Remove a program from user's preferences
   */
  static async removeProgramFromPreferences(programId: string): Promise<void> {
    try {
      const currentPreferences = await this.getUserProgramPreferences();
      if (!currentPreferences) return;

      const selectedPrograms = currentPreferences.selectedPrograms.filter(id => id !== programId);
      const primaryProgram = currentPreferences.primaryProgram === programId 
        ? (selectedPrograms.length > 0 ? selectedPrograms[0] : undefined)
        : currentPreferences.primaryProgram;

      await this.updateUserProgramPreferences({
        selectedPrograms,
        primaryProgram
      });
    } catch (error) {
      console.error('Error removing program from preferences:', error);
      throw error;
    }
  }

  /**
   * Set primary program
   */
  static async setPrimaryProgram(programId: string): Promise<void> {
    try {
      const currentPreferences = await this.getUserProgramPreferences();
      if (!currentPreferences) return;

      await this.updateUserProgramPreferences({
        primaryProgram: programId
      });
    } catch (error) {
      console.error('Error setting primary program:', error);
      throw error;
    }
  }
}
